import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader, MessageCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getUserDataById } from '@/lib/userData'
import { useBookingSystem } from '@/contexts/BookingSystemContext'
import type { UserData, TattooTimelineEntry } from '@/types/models'
import { format } from 'date-fns'
import { TattooTimeline } from '@/components/dashboard/TattooTimeline'
import { AddTattooDialog } from '@/components/dashboard/AddTattooDialog'
import { TattooDetailDialog } from '@/components/dashboard/TattooDetailDialog'
import { AddHealedPhotoDialog } from '@/components/dashboard/AddHealedPhotoDialog'
import { CompletedBookingPrompt } from '@/components/dashboard/CompletedBookingPrompt'
import { useRealtimeClientRegistry } from '@/hooks/useRealtimeClientRegistry'
import { useRealtimeBooking } from '@/hooks/useRealtimeBooking'
import { auth } from '@/lib/firebase'

export default function BookingSystemClientDetailPage() {
  const navigate = useNavigate()
  const { clientId } = useParams<{ clientId: string }>()
  const { artistId } = useBookingSystem()
  const [userData, setUserData] = useState<UserData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Real-time listeners for registry and booking
  const { registry, loading: registryLoading } = useRealtimeClientRegistry(
    artistId || null,
    clientId || null
  )
  const { booking } = useRealtimeBooking(registry?.bookingId || null)

  // Tattoo timeline data from registry
  const tattoos = registry?.tattoos ? [...registry.tattoos].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()) : []
  
  const [addTattooOpen, setAddTattooOpen] = useState(false)
  const [tattooDetailOpen, setTattooDetailOpen] = useState(false)
  const [addHealedOpen, setAddHealedOpen] = useState(false)
  const [selectedTattoo, setSelectedTattoo] = useState<TattooTimelineEntry | null>(null)

  // Fetch user data once on mount/clientId change
  useEffect(() => {
    const fetchUserData = async () => {
      if (!clientId) {
        setError('Missing client ID')
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const userDataResult = await getUserDataById(clientId)
        if (userDataResult) {
          setUserData(userDataResult)
          setError(null)
        } else {
          setError('Client not found')
        }
      } catch (err) {
        console.error('Error fetching user data:', err)
        setError('Failed to load client information')
      } finally {
        setLoading(false)
      }
    }

    fetchUserData()
  }, [clientId])

  // Check if we should show timeline (after first booking is complete or if tattoos exist)
  const shouldShowTimeline = tattoos.length > 0 || (registry?.lastTattooDate !== null)

  // Check if there's a booking without photos uploaded yet
  const isBookingScheduled = booking && booking.scheduledTimestamp
  const bookingHasPhotos = tattoos.some(tattoo => tattoo.bookingId === booking?.id)
  const shouldShowCompletedPrompt = isBookingScheduled && !bookingHasPhotos

  const handleTattooClick = (tattoo: TattooTimelineEntry) => {
    setSelectedTattoo(tattoo)
    setTattooDetailOpen(true)
  }

  const handleAddHealed = (tattoo: TattooTimelineEntry) => {
    setSelectedTattoo(tattoo)
    setAddHealedOpen(true)
  }

  const handleTattooSuccess = () => {
    // No need to manually refetch - real-time listeners will update automatically
  }

  const handleAddBookingPhotos = () => {
    setAddTattooOpen(true)
  }

  if (loading || registryLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="phone-container-padded pt-4 pb-0">
          <div className="mb-3 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="h-10 w-10 rounded-lg transition-all hover:shadow-lg bg-background dark:bg-card border border-border flex items-center justify-center"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5 text-foreground" />
            </button>
            <h1 className="text-page-title text-foreground">Client Details</h1>
          </div>
        </div>
        <div className="phone-container-padded">
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
            {error || 'Client not found'}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
      {/* Header */}
      <div className="phone-container-padded pt-4 pb-4">
        <div className="mb-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-lg transition-all hover:shadow-lg bg-background dark:bg-card border border-border flex items-center justify-center"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5 text-foreground" />
          </button>
          <h1 className="text-page-title text-foreground">You and {userData.instagram ? `@${userData.instagram}` : userData.displayName || userData.email}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="phone-container-padded pb-4">
        <div className="space-y-4">
          {/* Profile Section - No container */}
          <div>
            <div className="flex items-stretch gap-4 mb-6">
              {/* Avatar - Square */}
              <div className="w-24 h-24 rounded-[var(--radius)] bg-primary flex items-center justify-center overflow-hidden flex-shrink-0">
                {userData.avatarUrl ? (
                  <img
                    src={userData.avatarUrl}
                    alt={userData.displayName || 'Client'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-primary-foreground text-4xl font-semibold">
                    {(userData.displayName || userData.email || 'C').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Info on the right - fits in avatar height */}
              <div className="flex-1 min-w-0 flex flex-col py-0">
                {/* Name with first name and last initial */}
                {userData.firstName && (
                  <h2 className="text-base font-semibold truncate leading-tight">
                    {userData.firstName} {userData.lastName ? userData.lastName.charAt(0) + '.' : ''}
                  </h2>
                )}

                {/* Instagram handle as secondary info */}
                {userData.instagram && (
                  <p className="text-xs text-muted-foreground truncate leading-tight">
                    @{userData.instagram}
                  </p>
                )}

                {/* Booking or last tattoo info */}
                {booking && booking.scheduledTimestamp > new Date() ? (
                  <div className="text-xs font-medium text-green-500 leading-tight">
                    Booked for {format(booking.scheduledTimestamp, 'MMM d')}
                  </div>
                ) : registry?.lastTattooDate ? (
                  <div className="text-xs text-muted-foreground leading-tight">
                    Last tattoo was on {format(registry.lastTattooDate, 'MMM d')}
                  </div>
                ) : null}

                {/* Tattoo count */}
                {registry?.chatId && (
                  <button
                    onClick={() => navigate(`/chats/${registry.chatId}`)}
                    className="mt-auto flex items-center gap-2 p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors justify-center"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="font-medium text-sm">Open Chat</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Completed Booking Prompt */}
          {shouldShowCompletedPrompt && (
            <div className="mt-6">
              <CompletedBookingPrompt
                booking={booking!}
                onAddPhotos={handleAddBookingPhotos}
              />
            </div>
          )}

          {/* Tattoo Timeline Section */}
          {shouldShowTimeline && (
            <div className="mt-8">
              <TattooTimeline
                artistId={artistId || ''}
                clientId={clientId || ''}
                tattoos={tattoos}
                isArtist={true}
                onAddTattoo={() => setAddTattooOpen(true)}
                onTattooClick={handleTattooClick}
                onAddHealed={handleAddHealed}
              />
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <AddTattooDialog
        open={addTattooOpen}
        onOpenChange={setAddTattooOpen}
        artistId={artistId || ''}
        clientId={clientId || ''}
        booking={shouldShowCompletedPrompt ? booking : undefined}
        onSuccess={handleTattooSuccess}
      />

      <TattooDetailDialog
        open={tattooDetailOpen}
        onOpenChange={setTattooDetailOpen}
        tattoo={selectedTattoo}
        onAddHealed={selectedTattoo ? () => handleAddHealed(selectedTattoo) : undefined}
      />

      <AddHealedPhotoDialog
        open={addHealedOpen}
        onOpenChange={setAddHealedOpen}
        artistId={artistId || ''}
        clientId={clientId || ''}
        tattoo={selectedTattoo}
        currentUserId={auth.currentUser?.uid || ''}
        onSuccess={handleTattooSuccess}
      />
    </div>
  )
}

